export type NodeId =
  | "extract"
  | "classify"
  | "map"
  | "activation"
  | "resolve"
  | "bom"
  | "report";

export type NodeStatus =
  | "done"
  | "active"
  | "pending"
  | "error"
  | "paused"
  /** #854: a Fase A terminou e a decisão de escopo está em aberto. */
  | "awaiting"
  /**
   * A entrega terminou no servidor e o resultado ainda está chegando na tela
   * (o detalhe da execução tem dezenas de MB). Não é "rodando": o trabalho
   * acabou. Nem "pendente": ele aconteceu.
   */
  | "loading";

export type NodePhase = "Prepare" | "Analyze" | "Decide" | "Output";

export type RenderKind =
  | "kv"
  | "tiles"
  | "bars"
  | "rows"
  | "logs"
  | "table"
  | "tiles+table"
  | "note+table"
  | "markdown";

export interface TabSpec {
  id: string;
  label: string;
  count?: string | number;
}

export interface NodeSpec {
  id: NodeId;
  num: string;
  name: string;
  phase: NodePhase;
  fn: string;
  stateKey: string;
  defaultTab: string;
  tabs: TabSpec[];
}

export const NODES: NodeSpec[] = [
  {
    id: "extract",
    num: "01",
    name: "Extract ERP Data",
    phase: "Prepare",
    fn: "extract_ecc.py",
    stateKey: "extraction",
    defaultTab: "preview",
    tabs: [
      { id: "preview", label: "Frontend Preview" },
      { id: "rfc", label: "RFC Pulls" },
      { id: "sample", label: "Sample Data" },
    ],
  },
  {
    id: "classify",
    num: "02",
    name: "Classify Modules",
    phase: "Analyze",
    fn: "classify.py",
    stateKey: "classification",
    defaultTab: "preview",
    tabs: [
      { id: "preview", label: "Frontend Preview" },
      { id: "users", label: "User Classifications" },
      { id: "modules", label: "Modules in Scope" },
      { id: "custom", label: "Custom Z/Y" },
    ],
  },
  {
    id: "map",
    num: "03",
    name: "Map Scope Items",
    phase: "Analyze",
    fn: "map_scope.py",
    stateKey: "scope_mapping",
    defaultTab: "preview",
    tabs: [
      { id: "preview", label: "Frontend Preview" },
      { id: "items", label: "Scope Items" },
      { id: "unmapped", label: "Unmapped" },
    ],
  },
  {
    id: "activation",
    num: "04",
    name: "Fit-to-Standard",
    phase: "Analyze",
    fn: "assess_activation.py",
    stateKey: "scope_activation",
    defaultTab: "overview",
    tabs: [
      { id: "overview", label: "Overview" },
      { id: "decisions", label: "Decisions" },
      { id: "catalog", label: "Catalog" },
      // EF-FUNC-011 RF-03: a curadoria dos scope items e, em seguida, os GAPs.
      { id: "gaps", label: "GAP register" },
      { id: "consolidated", label: "Consolidated" },
    ],
  },
  {
    id: "resolve",
    num: "05",
    name: "Resolve SKUs",
    phase: "Decide",
    fn: "resolve_skus.py",
    stateKey: "sku_resolution",
    defaultTab: "preview",
    tabs: [
      { id: "preview", label: "Frontend Preview" },
      { id: "users", label: "User → SKU" },
      { id: "bom", label: "BOM Preview" },
      { id: "amb", label: "Ambiguous" },
    ],
  },
  {
    id: "bom",
    num: "06",
    name: "Generate BOM",
    phase: "Output",
    fn: "generate_bom.py",
    stateKey: "bom",
    defaultTab: "lines",
    tabs: [
      { id: "lines", label: "BOM Summary" },
      { id: "totals", label: "Totals" },
      { id: "excel", label: "Excel · 7 tabs" },
    ],
  },
  {
    id: "report",
    num: "07",
    name: "Generate Report",
    phase: "Output",
    fn: "generate_report.py",
    stateKey: "outputs",
    defaultTab: "report",
    tabs: [
      { id: "report", label: "Mapping Report" },
      { id: "metrics", label: "Metrics" },
      { id: "audit", label: "Audit Bundle" },
      { id: "fsm", label: "FSM Close" },
    ],
  },
];

/**
 * Contexto do i18next (`t(node.name, { context: NODE_NAME_I18N_CONTEXT })`)
 * para o nome do node no workbench do Functional.
 *
 * "Fit-to-Standard" também é o `title` de um card do roteiro SAP Activate no
 * Ato 4 do Diagnóstico (`diagnostic/acts.ts`, fase "explore"), que quer uma
 * tradução amigável ("Aderência ao padrão"). Como a chave de i18n aqui é o
 * próprio texto em inglês (sem namespace), as duas telas bateriam na mesma
 * entrada do bundle. O contexto separa os dois: só o nome do node passa
 * `{context}`, então só ele procura `"Fit-to-Standard_functional_node"`
 * primeiro — o card do Diagnóstico, que chama `t(phase.title)` sem contexto,
 * nunca vê essa entrada e continua recebendo a tradução amigável de sempre.
 *
 * Só "Fit-to-Standard" tem entrada de contexto nos bundles hoje; os outros 6
 * nomes de node caem no fallback (chave sem sufixo) porque não colidem com
 * nada — aplicar o contexto neles também é seguro e não muda nada.
 */
export const NODE_NAME_I18N_CONTEXT = "functional_node";

/**
 * Contexto i18next para os rótulos de status do node no `NodeStageCard`
 * (`t(STATUS_LABEL[status], { context: NODE_STATUS_I18N_CONTEXT })`).
 *
 * Evita colisões de chaves globais (ex: "pending" trazia "pendente(s)" no plural).
 * Isola os 6 status do card para não dependerem de coincidências de tradução de outras telas.
 */
export const NODE_STATUS_I18N_CONTEXT = "functional_node_status";

export const NODE_BY_ID: Record<NodeId, NodeSpec> = NODES.reduce(
  (acc, n) => {
    acc[n.id] = n;
    return acc;
  },
  {} as Record<NodeId, NodeSpec>,
);

export const NODE_PHASE_INDEX: Record<NodeId, number> = NODES.reduce(
  (acc, n, i) => {
    acc[n.id] = i;
    return acc;
  },
  {} as Record<NodeId, number>,
);

/**
 * Mapeia o nome do nó no graph do agent (e.g. "resolve_skus") para o
 * NodeId do rail (e.g. "resolve"). Usado pelo workbench quando o
 * backend emite `human_input_required.node_name` ou quando rehidrata
 * uma execução pausada (S-021 HITL-3.5 / 4.2).
 */
export const GRAPH_NAME_TO_NODE_ID: Record<string, NodeId> = {
  extract_ecc_data: "extract",
  classify_modules: "classify",
  map_scope_items: "map",
  assess_activation: "activation",
  resolve_skus: "resolve",
  generate_bom: "bom",
  generate_report: "report",
};

/**
 * Nós da fase de levantamento (#854). Os outros três rodam na entrega, numa
 * execução separada que a aprovação do escopo dispara.
 */
export const ASSESSMENT_NODE_IDS: readonly NodeId[] = [
  "extract",
  "classify",
  "map",
  "activation",
];

/**
 * A execução chegou a gerar entregável?
 *
 * Não dá para responder por presença de chave: `build_output_summary` preenche
 * as seções ausentes com vazio (array para o BOM, objeto para o SKU), então
 * uma run que parou no nó 04 tem `bom_summary` do mesmo jeito. O que distingue
 * é conteúdo.
 */
export function hasDeliveryOutput(outputs: unknown): boolean {
  if (!outputs || typeof outputs !== "object") return false;
  const o = outputs as Record<string, unknown>;

  const bom = o.bom_summary;
  if (Array.isArray(bom) ? bom.length > 0 : bom && typeof bom === "object") {
    return Object.keys(bom as Record<string, unknown>).length > 0;
  }
  return typeof o.report === "string" && o.report.length > 0;
}

/**
 * Status de cada nó numa run **concluída**.
 *
 * Sem entrega, a run é a Fase A: ela parou no nó 04 esperando a decisão de
 * escopo, que leva dias. Marcar os sete como concluídos faria a tela afirmar
 * que gerou BOM e relatório justamente quando não gerou nenhum dos dois.
 *
 * `scopeApproved` é do **projeto**, e `deliveryDone` é da **execução**. As duas
 * respondem perguntas diferentes: a primeira diz se a decisão saiu, a segunda
 * se este run em particular materializou o entregável.
 *
 * `deliveryStatus` é o job da fila. Ele existe porque a entrega roda fora da
 * sessão: sem ele os nós 05 a 07 ficam mudos entre a aprovação e o resultado, e
 * uma entrega que falhou fica igual a uma que está demorando.
 */
export type DeliveryStatus = "pending" | "running" | "done" | "failed" | null;

export function completedRunStatuses(
  deliveryDone: boolean,
  scopeApproved: boolean,
  deliveryStatus: DeliveryStatus = null,
): Record<NodeId, NodeStatus> {
  // Os nós da entrega, em ordem de quem tem a palavra final. O job manda sobre
  // o artefato: durante uma reentrega existe BOM na tela, mas é o da aprovação
  // anterior, prestes a ser substituído.
  const deliveryNodeStatus = (): NodeStatus => {
    // `pending` entra junto com `running`: o job já existe, o worker ainda não
    // o pegou, e o artefato na tela é o da aprovação anterior.
    if (deliveryStatus === "pending" || deliveryStatus === "running")
      return "active";
    if (deliveryStatus === "failed") return "error";
    // Job fechado e payload ainda voltando do servidor: carregando. Cair para
    // "pendente" nessa janela piscava uma informação falsa, porque o trabalho já
    // tinha terminado; "rodando" era a mesma mentira, ao contrário.
    if (deliveryStatus === "done" && !deliveryDone) return "loading";
    return deliveryDone ? "done" : "pending";
  };

  return NODES.reduce(
    (acc, node) => {
      if (node.id === "activation") {
        // O nó 04 responde **sempre** à aprovação do projeto, mesmo com
        // entregável presente: reabrir devolve a decisão para a mesa, e o BOM
        // que já existe passa a descrever um escopo que não vale mais.
        acc[node.id] = scopeApproved ? "done" : "awaiting";
      } else if (ASSESSMENT_NODE_IDS.includes(node.id)) {
        acc[node.id] = "done";
      } else {
        acc[node.id] = deliveryNodeStatus();
      }
      return acc;
    },
    {} as Record<NodeId, NodeStatus>,
  );
}

/**
 * Qual payload a tela deve ler: o do stream ou o persistido (#854).
 *
 * O hook da execução guarda em memória o `outputs` que veio pelo SSE da run. A
 * entrega roda num worker e grava **direto no banco**, sem passar por esse
 * canal, então o objeto em memória é o do levantamento e nunca vai ganhar o
 * BOM: ele só some num reload da página.
 *
 * Por isso a regra não é "persistido sempre ganha" — durante a run o stream é
 * mais novo que o banco, e trocar a precedência ali quebraria o acompanhamento
 * ao vivo. A troca acontece só no caso que a motivou: terminou a entrega, o
 * persistido tem entregável e o que está em memória não.
 */
export function resolveOutputs<T>(
  streamed: T | null,
  persisted: T | null,
  isRunning: boolean,
): T | null {
  if (isRunning) return streamed;
  if (hasDeliveryOutput(persisted) && !hasDeliveryOutput(streamed)) {
    return persisted;
  }
  return streamed ?? persisted;
}
